"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ReadyForADeviceUpgrade() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="221995" id="bereit-für-den-wandel" className="relative overflow-hidden bg-background py-24 text-foreground sm:py-32 rounded-3xl">
      <style>
        {`
          @keyframes panBackground {
            0% { background-position: 0px 0px; }
            100% { background-position: 100px 100px; }
          }
          .animate-circuit-pan {
            animation: panBackground 40s linear infinite;
          }
        `}
      </style>

      {}
      <div className="animate-circuit-pan absolute inset-0 opacity-10" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg stroke='%233b82f6' stroke-width='1' stroke-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3Ccircle cx='35' cy='33' r='1' fill='%233b82f6' fill-opacity='0.6'/%3E%3Ccircle cx='5' cy='33' r='1' fill='%233b82f6' fill-opacity='0.6'/%3E%3Ccircle cx='35' cy='3' r='1' fill='%233b82f6' fill-opacity='0.6'/%3E%3Ccircle cx='5' cy='3' r='1' fill='%233b82f6' fill-opacity='0.6'/%3E%3C/g%3E%3C/svg%3E")`,
      backgroundSize: "60px 60px"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-background to-background" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Bereit für den Wandel?
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground font-default">
            Gestalten Sie die Zukunft des Spendens mit Crypto4Good. Nutzen Sie die Vorteile der Blockchain-Technologie für maximale Transparenz und globale Wirkung Ihrer Philanthropie.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-x-6">
            <Button asChild size="lg" className="w-full bg-chart-2 text-primary-foreground hover:bg-chart-2/90 sm:w-auto font-default shadow-lg">
              <Link to="/DonorsPage">
                <RefreshCw className="mr-2 h-5 w-5" />
                Für Donoren
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full border-primary/50 bg-background/30 text-foreground backdrop-blur-sm hover:bg-primary hover:text-primary-foreground sm:w-auto font-default">
              <Link to="/FoundationsPage">
                Für Stiftungen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
          
          <div className="mt-12 flex items-center justify-center gap-x-8 text-sm font-medium text-muted-foreground sm:gap-x-12">
             <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-chart-2" />
                Schweizer Präzision
             </div>
             <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-chart-2" />
                Volle Transparenz
             </div>
             <div className="hidden items-center gap-2 sm:flex">
                <div className="h-2 w-2 rounded-full bg-chart-2" />
                Sichere Abwicklung
             </div>
          </div>
        </div>
      </div>
    </section>;
}